// @flow

import { localStorage } from "utils/storage";
import { push } from "react-router-redux";
import { ACCESS_TOKEN } from "utils/http";
import { SubmissionError } from "redux-form";
import { get } from "lodash";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import * as API from "utils/webAPI";
import { getLang } from "utils/language";
import { getCountDecimals, getCurrencySymbol } from "utils/currencyHelper";
import * as ActionTypes from "./constants";

import {
  sumbitSendInvest,
  sumbitPerformWithdraw
} from "./actions";

function* userLogin({ code, lang }) {
  try {
    const { data } = yield call(API.login, code);
    if (data.access_token) {
      yield localStorage.setItem(ACCESS_TOKEN, data.access_token);
      yield put({ type: ActionTypes.USER_LOGIN_SUCCESS, data: { ...data.user } });
      yield put(push(`/${lang}/dashboard/`));
    }
  } catch (err) {
    yield put(push(`/${lang}/dashboard/`));
  }
}

function* sendInvest({ payload: invest }) {
  try {
    if (invest.amount <= 0.00) throw Error("Amount is not correct");
    if (!invest.currency) throw Error("Currency is not correct");

    invest.amount = invest.amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
    const prevData = yield call(API.getDashboard);
    if (prevData) {
      const { data } = yield call(
        prevData.data.invest && prevData.data.invest.amount > 0.00 ? API.modifyInvest : API.sendInvest,
        {
          amount: parseFloat(invest.amount),
          currency: invest.currency
        }
      );

      yield put(sumbitSendInvest.success());
      yield put(push(`/${getLang()}/dashboard/`));
      yield put({ type: ActionTypes.USER_ME_SUCCESS, data: { ...data } });
    }
  } catch (e) {
    const formError = new SubmissionError({
      invest: 'invest with this amount is not allowed!', // specific field error
      _error: 'Investment failed, please check your amount/currency and try again', // global form error
    });
    yield put(sumbitSendInvest.failure(formError));
    yield put({ type: ActionTypes.USER_LOGIN_FAILURE, data: { error: e } });
  }
}

function* userLogout() {
  try {
    yield put({ type: ActionTypes.USER_LOGOUT_SUCCESS });
    yield localStorage.removeItem(ACCESS_TOKEN);
    yield put(push(`/${getLang()}/`));
  } catch (err) {
    yield put({ type: ActionTypes.USER_LOGOUT_FAILURE, data: err.message });
  }
}

function* performWithdraw({ payload: withdraw }) {
  try {
    if (withdraw.withdraw > 0) {

      const { data } = yield call(API.sendWithdraw, {
        amount: parseInt(withdraw.withdraw, 10),
        currency: withdraw.currency
      });

      yield put(sumbitPerformWithdraw.success());
      yield put({ type: ActionTypes.USER_ME_SUCCESS, data: { ...data } });
    }
  } catch (err) {
    const formError = new SubmissionError({
      _error: `${err.data.statusCode}`
    });
    yield put(sumbitPerformWithdraw.failure(formError));
  }
}

function* deleteInvest({ payload }) {
  const redirectToDashboard = get(payload, "redirectToDashboard", false);

  try {
    const accessToken = yield localStorage.getItem(ACCESS_TOKEN);
    if (accessToken) {
      const { data } = yield call(API.deleteInvest);
      yield put({ type: ActionTypes.DELETE_INVEST_SUCCESS });
      yield put({ type: ActionTypes.USER_ME_SUCCESS, data: { ...data } });
      if (redirectToDashboard) {
        yield put(push(`/${getLang()}/dashboard/`));
      }
    }
  } catch (err) {
    yield put({ type: ActionTypes.DELETE_INVEST_FAILURE, data: err.message });
  }
}

function* cancelWithdraw() {
  try {
    const id = yield localStorage.access_token;
    if (id) {
      const { data } = yield call(API.cancelWithdraw);
      yield put({ type: ActionTypes.CANCEL_WITHDRAW_SUCCESS });
      yield put({ type: ActionTypes.USER_ME_SUCCESS, data: { ...data } });
    }
  } catch (err) {
    yield put({ type: ActionTypes.CANCEL_WITHDRAW_FAILURE, data: err.message });
  }
}

function* getNotification() {
  try {
    const { data } = yield call(API.getNotification);
    const notifications = data.map((item) => {
      const body = JSON.parse(item.body);
      const countDecimals = getCountDecimals(get(body, "amount"), 2);

      return {
        id: item.id,
        date: new Date(item.createdAt).toLocaleDateString(),
        type: item.statusCode,
        amount: body.amount ? getCurrencySymbol(body.currency) + body.amount.toLocaleString("en-US", {
          minimumFractionDigits: countDecimals,
          maximumFractionDigits: countDecimals
        }) : null,
        currency: get(body, "currency", null)
      };
    });
    yield put({ type: ActionTypes.GET_NOTIFICATION_SUCCESS, data: [...notifications] });
    yield put.resolve({ type: ActionTypes.CLEAR_UNREAD_NOTIFICATIONS, data: { countNewNotification: 0 } });
  } catch (err) {
    yield put({ type: ActionTypes.USER_LOGOUT_FAILURE, data: err.message });
  }
}

function* getTickers() {
  try {
    const { data } = yield call(API.getTickers);
    if(data) {
      yield put({ type: ActionTypes.GET_TICKERS_SUCCESS, data: [...data.reverse()] });
    }

  } catch (err) {
    yield put({ type: ActionTypes.GET_TICKERS_FAILURE, data: err.message });
  }
}

function* getTransactions() {
  try {
    const { data } = yield call(API.getTransactions);
    if(data) {
      yield put({ type: ActionTypes.GET_TRANSACTIONS_SUCCESS, data: [...data] });
    }

  } catch (err) {
    yield put({ type: ActionTypes.GET_TRANSACTIONS_FAILURE, data: err.message });
  }
}

function* getDashboard() {
  try {
    const { data } = yield call(API.getDashboard);
    if (data) {
      yield put({ type: ActionTypes.USER_ME_SUCCESS, data: { ...data } });
    }
  } catch (err) {
    yield put({ type: ActionTypes.USER_LOGOUT_FAILURE, data: err.message });
  }
}

function* setMarketingEmailConsentInSettings({boolVal}) {
  try {
    const { data } = yield call(API.setMarketingEmailConsentInSettings, boolVal);
    console.log("setMarketingEmailConsentInSettings:", boolVal, "data:", data);
  } catch (err) {
    console.log(err);
  }
}

export default [
  takeLatest(ActionTypes.USER_LOGOUT_REQUEST, userLogout),
  takeLatest(ActionTypes.USER_LOGIN_REQUEST, userLogin),
  takeLatest(ActionTypes.GET_NOTIFICATION_REQUEST, getNotification),
  takeLatest(ActionTypes.GET_DASHBORD_REQUEST, getDashboard),
  takeLatest(ActionTypes.GET_TICKERS_REQUEST, getTickers),
  takeLatest(ActionTypes.GET_TRANSACTIONS_REQUEST, getTransactions),
  takeLatest(ActionTypes.DELETE_INVEST_REQUEST, deleteInvest),
  takeLatest(ActionTypes.CANCEL_WITHDRAW_REQUEST, cancelWithdraw),
  takeLatest(sumbitPerformWithdraw.REQUEST, performWithdraw),
  takeLatest(ActionTypes.SET_EMAIL_REQUEST, setMarketingEmailConsentInSettings),
  takeEvery(sumbitSendInvest.REQUEST, sendInvest)
];



// WEBPACK FOOTER //
// ./src/ducks/user/saga.js